import { useMemo, useCallback, useEffect } from 'react'
import { useMap } from '@vis.gl/react-google-maps'
import Supercluster, { ClusterProperties } from 'supercluster'
import { Feature, GeoJsonProperties, Point } from 'geojson'
import { useFlyTo, cameraForLocations, useSupercluster } from '../Hooks'
import { MarkerProps, MARKER_TYPE } from '../Marker'
import { ClusterProps } from './Cluster.types'
import { COLORS } from '@/styles'
import { MILES_UNIT_CONVERSION } from '../Map.data'

const superclusterOptions: Supercluster.Options<GeoJsonProperties, ClusterProperties> = {
  extent: 256,
  radius: 80,
  maxZoom: 20,
  minPoints: 5,
}

export const useClusterController = ({
  locations,
  selectedMarker,
  onMarkerClose,
  onMarkerClick,
  autoFitPinZoom = false,
  circleRadiusInMiles,
  centerLocation,
}: ClusterProps) => {
  const map = useMap()
  const flyTo = useFlyTo()

  const { processedGeojson, markers } = useMemo(() => {
    const clusterFeatures: Feature<Point, MarkerProps>[] = []
    const markerFeatures: Feature<Point, MarkerProps>[] = []

    locations.forEach((location, index) => {
      const feature: Feature<Point, MarkerProps> = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [location.location.lng, location.location.lat],
        },
        properties: location,
      }

      const excludeFromCluster = [MARKER_TYPE.VENUE, MARKER_TYPE.CITY_CENTER].includes(
        location.type,
      )

      if (excludeFromCluster) {
        feature.id = location.key || `marker-${index}`
        markerFeatures.push(feature)
      } else {
        feature.id = location.key || `cluster-${index}`
        clusterFeatures.push(feature)
      }
    })

    return {
      processedGeojson: {
        type: 'FeatureCollection' as const,
        features: clusterFeatures,
      },
      markers: markerFeatures,
    }
  }, [locations])

  const { clusters, clusterer } = useSupercluster<MarkerProps>(
    processedGeojson,
    superclusterOptions,
  )

  const handleMarkerOnClick = useCallback(
    (marker: MarkerProps | null = null) => {
      if (marker && selectedMarker?.key !== marker.key) {
        onMarkerClick?.(marker)
        if (map && marker.location) map.panTo(marker.location)
      } else onMarkerClose?.()
    },
    [map, selectedMarker, onMarkerClick, onMarkerClose],
  )

  const handleClusterClick = useCallback(
    (clusterId: number) => {
      if (!clusterer) return

      const clusterLeaves = clusterer.getLeaves(clusterId, Infinity)
      const clusterPoints = clusterLeaves.map((leaf) => leaf.properties as MarkerProps)

      if (map) {
        const bounds = new google.maps.LatLngBounds()
        clusterPoints.forEach((point) => bounds.extend(point.location))
        map.fitBounds(bounds)
      }
    },
    [map, clusterer],
  )

  /*
   * Auto-fit the map view to show all location markers when autoFitPinZoom is enabled.
   * The camera flies (animated pan + zoom) to a fixed focus level for a single
   * marker, or to a bounds-fitting frame around several.
   */
  useEffect(() => {
    if (!autoFitPinZoom || !map || locations.length === 0) return

    const timer = setTimeout(() => {
      const target = cameraForLocations(
        map,
        locations.map((point) => point.location),
      )
      if (target) flyTo(target)
    }, 100)

    return () => clearTimeout(timer)
  }, [autoFitPinZoom, map, locations, flyTo])

  /*
   * Create and manage a circular overlay on the map to show the search radius area
   * This effect renders a semi-transparent circle centered at the specified location with the given radius in miles
   * The circle is created with a 100ms delay and automatically cleaned up when dependencies change
   */
  useEffect(() => {
    if (!map || !circleRadiusInMiles) return

    let circle: google.maps.Circle | null = null

    const timer = setTimeout(() => {
      circle = new google.maps.Circle({
        map,
        center: { lat: centerLocation.lat, lng: centerLocation.lng },
        radius: circleRadiusInMiles * MILES_UNIT_CONVERSION.meters,
        strokeColor: COLORS.AZURE_SKY,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: COLORS.AZURE_SKY,
        fillOpacity: 0.05,
        clickable: false,
      })
    }, 100)

    return () => {
      clearTimeout(timer)
      if (circle) circle.setMap(null)
    }
  }, [map, circleRadiusInMiles, centerLocation.lat, centerLocation.lng])

  return {
    clusters: [...markers, ...clusters],
    handleMarkerOnClick,
    handleClusterClick,
  }
}
